
import React from "react";
import { Check, XCircle, TrendingUp, Shield } from "lucide-react";

const XDRIntroductionSection: React.FC = () => {
  return (
    <section className="py-16 bg-white" id="introduction">
      <div className="container mx-auto px-4 md:px-6">
        <div className="max-w-4xl mx-auto">
          <article className="prose lg:prose-lg max-w-none">
            
            {/* Hook */}
            <div className="mb-12 p-8 bg-slate-50 rounded-lg border-l-4 border-[#8932c8]">
              <h2 className="text-2xl font-bold mb-4 text-gray-900">The Strategic Imperative</h2>
              <p className="text-lg text-gray-700 mb-4">
                <strong>Here's a question every CISO should ask:</strong> Are you still fighting tomorrow's battles with yesterday's weapons?
              </p>
              <p className="text-lg text-gray-700">
                While 73% of organizations report feeling overwhelmed by security alerts, the most successful enterprises have quietly revolutionized their approach through AI-powered Extended Detection and Response (XDR) strategies. They're not just detecting threats faster - they're fundamentally rethinking what cybersecurity leadership means in an AI-first world.
              </p>
            </div>

            {/* Context Setting */}
            <h2 className="text-3xl font-bold mb-6" id="strategic-context">Reframing the Cybersecurity Challenge</h2>
            
            <p className="text-lg text-gray-700 mb-6">
              The traditional cybersecurity paradigm is failing. Not because the tools are inadequate, but because the strategic framework itself is fundamentally misaligned with modern threat realities. While most organizations focus on reactive defense, threat actors have evolved into sophisticated, AI-enabled adversaries operating with strategic patience and precision.
            </p>

            <div className="bg-blue-50 p-6 rounded-lg border border-blue-200 mb-8">
              <div className="flex items-start">
                <TrendingUp className="h-6 w-6 text-blue-600 mr-3 mt-1" />
                <div>
                  <h3 className="font-semibold text-blue-800 mb-2">The Strategic Shift</h3>
                  <p className="text-blue-700">
                    Leading organizations are moving from <em>reactive security management</em> to <em>proactive threat intelligence</em> - transforming cybersecurity from a cost center into a strategic business enabler through AI-powered XDR implementation.
                  </p>
                </div>
              </div>
            </div>

            {/* What XDR Really Means */}
            <h2 className="text-3xl font-bold mb-6" id="xdr-redefined">Extended Detection and Response: Beyond the Technology</h2>
            
            <p className="text-lg text-gray-700 mb-6">
              Extended Detection and Response isn't merely a technology upgrade - it represents a fundamental shift in cybersecurity philosophy. While traditional security approaches create data silos and fragmented visibility, XDR establishes a unified intelligence layer that mirrors how modern threats actually operate: across boundaries, through time, and with sophisticated coordination.
            </p>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-10">
              <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                <div className="flex items-center mb-4">
                  <XCircle className="h-6 w-6 text-red-500 mr-3" />
                  <h3 className="text-xl font-semibold text-red-800">Legacy Security Thinking</h3>
                </div>
                <ul className="space-y-3 text-red-700">
                  <li>• Point solutions creating blind spots</li>
                  <li>• Reactive incident response cycles</li>
                  <li>• Alert fatigue overwhelming analysts</li>
                  <li>• Disconnected threat intelligence</li>
                  <li>• Manual correlation across systems</li>
                </ul>
              </div>

              <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                <div className="flex items-center mb-4">
                  <Shield className="h-6 w-6 text-green-500 mr-3" />
                  <h3 className="text-xl font-semibold text-green-800">Strategic XDR Leadership</h3>
                </div>
                <ul className="space-y-3 text-green-700">
                  <li>• Holistic threat landscape visibility</li>
                  <li>• Predictive threat modeling</li>
                  <li>• Intelligent alert prioritization</li>
                  <li>• Contextual threat intelligence</li>
                  <li>• Automated investigation workflows</li>
                </ul>
              </div>
            </div>

            {/* Strategic Context */}
            <div className="bg-slate-50 p-6 rounded-lg border border-slate-200 mb-10">
              <h3 className="text-lg font-bold mb-3 text-slate-800">Why This Transformation Matters Now</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold mb-2 text-slate-700">Business Context</h4>
                  <ul className="space-y-1 text-sm text-slate-600">
                    <li>• Digital transformation expanding attack surfaces</li>
                    <li>• Remote work creating new vulnerabilities</li>
                    <li>• Regulatory compliance becoming more stringent</li>
                    <li>• Cyber insurance requiring advanced controls</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold mb-2 text-slate-700">Threat Evolution</h4>
                  <ul className="space-y-1 text-sm text-slate-600">
                    <li>• AI-powered attack automation</li>
                    <li>• Nation-state threat sophistication</li>
                    <li>• Supply chain attack proliferation</li>
                    <li>• Ransomware-as-a-Service industrialization</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Strategic Insight */}
            <div className="border-l-4 border-[#8932c8] pl-6 mb-8">
              <h3 className="text-xl font-semibold mb-3 text-gray-900">Strategic Insight: The Network Effect of Security</h3>
              <p className="text-lg text-gray-700 mb-4">
                The most successful XDR implementations don't just connect security tools - they create a <strong>network effect</strong> where each additional data source exponentially increases the value of threat intelligence across the entire security ecosystem.
              </p>
              <p className="text-lg text-gray-700">
                This principle, borrowed from platform economics, explains why organizations with mature XDR strategies report not just incremental improvements, but transformational changes in their security posture and operational efficiency.
              </p>
            </div>

          </article>
        </div>
      </div>
    </section>
  );
};

export default XDRIntroductionSection;
